import * as React from 'react'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import type { ButtonProps } from '#/shared/ui/button'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

type Align = 'left' | 'center' | 'right'

export interface ActionMenuItem {
  value: string
  label: string
}

export interface ActionMenuProps {
  label: string
  items: ActionMenuItem[]
  onSelect?: (value: string) => void
  align?: Align
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  triggerClassName?: string
  triggerStartIcon?: React.ReactNode
  panelClassName?: string
  autoFocus?: boolean
}

function ActionMenuPanel({
  items,
  open,
  autoFocus,
  onSelect,
  onClose,
  className,
}: {
  items: ActionMenuItem[]
  open: boolean
  autoFocus: boolean
  onSelect?: (value: string) => void
  onClose: () => void
  className?: string
}) {
  const firstItemRef = React.useRef<HTMLButtonElement | null>(null)
  React.useEffect(() => {
    if (open && autoFocus) firstItemRef.current?.focus()
  }, [open, autoFocus])
  return (
    <div
      className={cn(
        'flex w-fit flex-col gap-0.5 rounded-md bg-white p-1 whitespace-nowrap shadow-popover',
        className,
      )}
    >
      {items.map((item, idx) => (
        <button
          key={item.value}
          ref={idx === 0 ? firstItemRef : undefined}
          type="button"
          onClick={() => {
            onSelect?.(item.value)
            onClose()
          }}
          className="flex h-8 w-full cursor-pointer items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

export function ActionMenu({
  label,
  items,
  onSelect,
  align = 'center',
  variant = 'default',
  size = 'xs',
  triggerClassName,
  triggerStartIcon,
  panelClassName,
  autoFocus = true,
}: ActionMenuProps) {
  return (
    <div className="w-fit">
      <Popover
        align={align}
        trigger={(open) => (
          <Button
            variant={variant}
            size={size}
            className={cn(
              'px-2.5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:focus-ring',
              open && 'focus-ring',
              triggerClassName,
            )}
          >
            {triggerStartIcon}
            {label}
            <ChevronDownIcon
              className={cn(
                'size-2.5 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </Button>
        )}
      >
        {(close, open) => (
          <ActionMenuPanel
            items={items}
            open={open}
            autoFocus={autoFocus}
            onSelect={onSelect}
            onClose={close}
            className={panelClassName}
          />
        )}
      </Popover>
    </div>
  )
}
